import { inject } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular/standalone';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { TiersService } from 'src/app/core/services/tiers/tiers.service';
import { TournamentDataModel } from 'src/app/core/services/tournament/models/tournament-data-model';
import {
  openTournamentLeaderboard,
  openTournamentTutorial,
} from 'src/app/features/tournaments/services/open-tournament-drawers';
import { GameId } from 'src/app/shared/models/game/game-id';
import { canShowLocalPlay } from '../utils/local-play.utils';

/**
 * The flags and output handlers that `app-wager-tier-list` expects from whatever hosts it, other
 * than the per-host tier taps.
 */
export interface TierListHost {
  readonly canShowLocal: boolean;
  readonly isMarbles: boolean;
  onCelebrationCleared(): void;
  onTutorialRequested(tournamentData: TournamentDataModel): Promise<void>;
  onLeaderboardRequested(tournamentData: TournamentDataModel): Promise<void>;
}

/**
 * Every tier-list host must take its tier-list wiring from here, in a field initialiser. Angular
 * can't make an output required, so a host that leaves `celebrationClearedEvent` or a drawer
 * request unbound still compiles and the tap silently does nothing; one implementation, plus a
 * host spec that drives each of those outputs into it, is what keeps the hosts in step.
 */
export function injectTierListHost(): TierListHost {
  const operatorService = inject(OperatorService);
  const tiersService = inject(TiersService);
  const modalController = inject(ModalController);
  const platform = inject(Platform);
  const themeService = inject(ThemeService);

  // Read on every check rather than once, as the operator's game is not a signal.
  const isMarbles = (): boolean => operatorService.gameId === GameId.Marbles;

  return {
    canShowLocal: canShowLocalPlay(() => operatorService.operatorId),
    get isMarbles(): boolean {
      return isMarbles();
    },
    onCelebrationCleared: (): void => {
      tiersService.clearCelebrations();
    },
    onTutorialRequested: (tournamentData: TournamentDataModel): Promise<void> =>
      openTournamentTutorial(modalController, platform, themeService, tournamentData, isMarbles()),
    onLeaderboardRequested: (tournamentData: TournamentDataModel): Promise<void> =>
      openTournamentLeaderboard(
        modalController,
        platform,
        themeService,
        tournamentData.tournamentId,
      ),
  };
}
